import React, { useState, useEffect } from 'react';
import { ScatterChart, Scatter, XAxis, YAxis, CartesianGrid, Tooltip, ResponsiveContainer, LineChart, Line, ReferenceLine, BarChart, Bar, Legend, RadarChart, Radar, PolarGrid, PolarAngleAxis } from 'recharts';

const API = '/api';

export default function Diagnostico() {
  const [busStats, setBusStats] = useState([]);
  const [tendencia, setTendencia] = useState([]);
  const [porRango, setPorRango] = useState([]);
  const [porMarca, setPorMarca] = useState([]);
  const [degradacion, setDegradacion] = useState([]);
  const [flotaKwhPorSoc, setFlotaKwhPorSoc] = useState(0);
  const [flotaKwProm, setFlotaKwProm] = useState(0);
  const [loading, setLoading] = useState(true);
  const [tab, setTab] = useState('ranking');

  useEffect(() => {
    fetch(`${API}/transactions`)
      .then(r => r.json())
      .then(data => {
        const txs = data
          .filter(t => t.soc_end > 5 && t.energy_delivered > 5 && t.soc_start >= 0)
          .map(t => {
            const deltaSoc = t.soc_end - t.soc_start;
            const durMin = t.start_time && t.stop_time
              ? Math.round((new Date(t.stop_time) - new Date(t.start_time)) / 60000) : 0;
            const kwhPorSoc = deltaSoc >= 20 ? parseFloat((t.energy_delivered / deltaSoc).toFixed(3)) : null;
            const kwProm = durMin > 0 ? parseFloat((t.energy_delivered / (durMin / 60)).toFixed(1)) : null;
            return {
              txId: t.tx_id, cpId: t.cp_id, cop: t.cop || 'Blanco y Negro',
              vin: t.vin, zonal: t.zonal, startTime: t.start_time,
              socStart: t.soc_start, socEnd: t.soc_end,
              energyDelivered: t.energy_delivered, duracion: durMin,
              deltaSoc, kwhPorSoc, kwProm,
            };
          })
          .filter(t => t.kwhPorSoc !== null && t.kwhPorSoc > 0 && t.kwhPorSoc < 10);

        const avg = arr => arr.length > 0 ? arr.reduce((a,b)=>a+b,0)/arr.length : 0;

        // Por bus
        const porBus = {};
        txs.forEach(t => {
          const key = t.zonal || t.vin || 'Desconocido';
          if (!porBus[key]) porBus[key] = { zonal:t.zonal, vin:t.vin, cop:t.cop, kwhArr:[], kwArr:[], energias:[], duraciones:[], sesiones:0, meses:{} };
          porBus[key].sesiones++;
          porBus[key].kwhArr.push(t.kwhPorSoc);
          if (t.kwProm) porBus[key].kwArr.push(t.kwProm);
          porBus[key].energias.push(t.energyDelivered);
          porBus[key].duraciones.push(t.duracion);
          // Para degradación — agrupar por mes
          const mes = t.startTime?.slice(0,7);
          if (mes) {
            if (!porBus[key].meses[mes]) porBus[key].meses[mes] = [];
            porBus[key].meses[mes].push(t.kwhPorSoc);
          }
        });

        const stats = Object.entries(porBus)
          .map(([key, d]) => ({
            key, zonal: d.zonal||key, vin: d.vin, cop: d.cop,
            sesiones: d.sesiones,
            avgKwhPorSoc: parseFloat(avg(d.kwhArr).toFixed(3)),
            avgKwProm: parseFloat(avg(d.kwArr).toFixed(1)),
            avgEnergia: Math.round(avg(d.energias)),
            avgDuracion: Math.round(avg(d.duraciones)),
            totalEnergia: Math.round(d.energias.reduce((a,b)=>a+b,0)),
            kmRecorridos: d.kmArr ? Math.round(d.kmArr.reduce((a,b)=>a+b,0)) : 0,
            meses: d.meses,
          }))
          .filter(b => b.sesiones >= 1 && b.avgKwhPorSoc > 0)
          .sort((a,b) => b.avgKwhPorSoc - a.avgKwhPorSoc);

        const allKwh = stats.map(b=>b.avgKwhPorSoc);
        const allKw = stats.map(b=>b.avgKwProm).filter(v=>v>0);
        const fKwh = parseFloat(avg(allKwh).toFixed(3));
        const fKw = parseFloat(avg(allKw).toFixed(1));

        // Tendencia en el tiempo
        const tend = txs.sort((a,b)=>new Date(a.startTime)-new Date(b.startTime))
          .map((t,i) => ({ idx:i+1, fecha:new Date(t.startTime).toLocaleDateString('es-CO'), kwhPorSoc:t.kwhPorSoc, zonal:t.zonal||'—' }));

        // Por rango de SoC
        const rangos = [
          { label:'0-20%', min:0, max:20, vals:[] },
          { label:'20-50%', min:20, max:50, vals:[] },
          { label:'50-80%', min:50, max:80, vals:[] },
          { label:'80-100%', min:80, max:100, vals:[] },
        ];
        txs.forEach(t => {
          const rango = rangos.find(r => t.socStart >= r.min && t.socStart < r.max);
          if (rango) rango.vals.push(t.kwhPorSoc);
        });
        const porRangoData = rangos.map(r => ({
          rango: r.label,
          eficiencia: parseFloat(avg(r.vals).toFixed(3)),
          sesiones: r.vals.length,
        })).filter(r => r.sesiones > 0);

        // Por marca — extraer de VIN o zonal
        const marcas = {};
        txs.forEach(t => {
          // BYD Z64 = MF, BYD Z92 = MU, Yutong = Z92 con patrón diferente
          let marca = 'BYD';
          if (t.vin?.includes('LC04S')) marca = 'Yutong E9';
          if (!marcas[marca]) marcas[marca] = { kwhArr:[], kwArr:[], sesiones:0, energias:[] };
          marcas[marca].sesiones++;
          marcas[marca].kwhArr.push(t.kwhPorSoc);
          if (t.kwProm) marcas[marca].kwArr.push(t.kwProm);
          marcas[marca].energias.push(t.energyDelivered);
        });
        const porMarcaData = Object.entries(marcas).map(([marca, d]) => ({
          marca,
          eficiencia: parseFloat(avg(d.kwhArr).toFixed(3)),
          potencia: parseFloat(avg(d.kwArr).toFixed(1)),
          sesiones: d.sesiones,
          energiaTotal: Math.round(d.energias.reduce((a,b)=>a+b,0)),
        }));

        // Degradación — eficiencia promedio por mes de toda la flota
        const mesesFlota = {};
        txs.forEach(t => {
          const mes = t.startTime?.slice(0,7);
          if (mes) {
            if (!mesesFlota[mes]) mesesFlota[mes] = [];
            mesesFlota[mes].push(t.kwhPorSoc);
          }
        });
        const degradData = Object.entries(mesesFlota)
          .sort(([a],[b])=>a.localeCompare(b))
          .map(([mes, vals]) => ({
            mes: mes.slice(5) + '/' + mes.slice(0,4),
            eficiencia: parseFloat(avg(vals).toFixed(3)),
            sesiones: vals.length,
          }));

        setBusStats(stats);
        setFlotaKwhPorSoc(fKwh);
        setFlotaKwProm(fKw);
        setTendencia(tend);
        setPorRango(porRangoData);
        setPorMarca(porMarcaData);
        setDegradacion(degradData);
        setLoading(false);
      })
      .catch(e => { console.error(e); setLoading(false); });
  }, []);

  const clasificar = b => {
    if (b.avgKwhPorSoc > 5.0) return { label:'Ineficiente', color:'#f87171', bg:'#3a1a1a' };
    if (b.avgKwhPorSoc > 4.0) return { label:'Revisar', color:'#f59e0b', bg:'#3a2a1a' };
    return { label:'Normal', color:'#4ade80', bg:'#1a3a2a' };
  };

  const pad = n => String(n).padStart(2,'0');
  const durStr = m => m>=60?`${Math.floor(m/60)}h ${pad(m%60)}m`:`${m}m`;
  const ineficientes = busStats.filter(b=>clasificar(b).label!=='Normal');
  const scatterData = busStats.map(b=>({x:b.avgKwProm,y:b.avgKwhPorSoc,zonal:b.zonal,clase:clasificar(b).label}));

  const CustomTooltip = ({active,payload}) => {
    if(!active||!payload?.length) return null;
    const d=payload[0]?.payload;
    return <div style={{background:'#1a1d27',border:'1px solid #2a2d3a',padding:'10px',borderRadius:'8px',fontSize:'12px'}}>
      <p style={{color:'#4ade80',fontWeight:'500'}}>{d?.zonal||d?.marca||d?.rango}</p>
      {d?.eficiencia && <p style={{color:'#aaa'}}>Eficiencia: {d.eficiencia} kWh/%</p>}
      {d?.potencia && <p style={{color:'#aaa'}}>Potencia: {d.potencia} kW</p>}
      {d?.sesiones && <p style={{color:'#aaa'}}>Sesiones: {d.sesiones}</p>}
      {d?.x && <p style={{color:'#aaa'}}>Potencia: {d.x} kW</p>}
      {d?.y && <p style={{color:'#aaa'}}>Eficiencia: {d.y} kWh/%</p>}
    </div>;
  };

  if (loading) return <div style={{padding:'3rem',textAlign:'center',color:'#666'}}>Analizando eficiencia de flota...</div>;

  const tabs = [
    {id:'ranking', label:'📊 Ranking'},
    {id:'rangos', label:'🔋 Por rango SoC'},
    {id:'marcas', label:'🚌 Por marca'},
    {id:'degradacion', label:'📉 Degradación'},
    {id:'dispersion', label:'🔵 Dispersión'},
    {id:'tendencia', label:'📈 Tendencia'},
  ];

  return (
    <div>
      <div className="page-title">Diagnóstico — Eficiencia de carga</div>

      <div className="metrics">
        <div className="metric-card"><div className="metric-label">Buses analizados</div><div className="metric-value">{busStats.length}</div></div>
        <div className="metric-card"><div className="metric-label">Eficiencia flota</div><div className="metric-value">{flotaKwhPorSoc}</div><div className="metric-sub">kWh por % SoC</div></div>
        <div className="metric-card"><div className="metric-label">Potencia prom.</div><div className="metric-value">{flotaKwProm} kW</div></div>
        <div className="metric-card"><div className="metric-label">Buses a revisar</div><div className="metric-value" style={{color:ineficientes.length>0?'#f59e0b':'#4ade80'}}>{ineficientes.length}</div></div>
      </div>

      {ineficientes.length > 0 && (
        <div style={{background:'#3a2a1a',border:'1px solid #f59e0b',borderRadius:'8px',padding:'12px 16px',marginBottom:'1.25rem',fontSize:'13px',color:'#fcd34d'}}>
          ⚠ {ineficientes.length} buses con eficiencia fuera del rango normal (&gt;4.0 kWh/%)
        </div>
      )}

      <div style={{display:'flex',gap:'6px',marginBottom:'1.25rem',flexWrap:'wrap'}}>
        {tabs.map(t=>(
          <button key={t.id} onClick={()=>setTab(t.id)}
            style={{padding:'6px 12px',border:'1px solid',borderRadius:'6px',cursor:'pointer',fontSize:'12px',
              background:tab===t.id?'#2a2d3a':'#1a1d27',borderColor:tab===t.id?'#4ade80':'#2a2d3a',color:tab===t.id?'#4ade80':'#666'}}>
            {t.label}
          </button>
        ))}
      </div>

      {/* RANKING */}
      {tab==='ranking' && (
        <div className="card">
          <div className="card-title">kWh por % SoC <span style={{color:'#555',fontWeight:'400'}}>(menor = más eficiente · Normal &lt;4.0 · Revisar 4-5 · Ineficiente &gt;5)</span></div>
          <table>
            <thead><tr><th>#</th><th>Bus/Zonal</th><th>COP</th><th>Sesiones</th><th>kWh/% SoC</th><th>vs Flota</th><th>kW prom.</th><th>Energía prom.</th><th>Dur. prom.</th><th>km/kWh</th><th>Estado</th></tr></thead>
            <tbody>
              {busStats.map((b,i)=>{
                const cls=clasificar(b);
                const diff=parseFloat((b.avgKwhPorSoc-flotaKwhPorSoc).toFixed(3));
                const diffPct=parseFloat(((diff/flotaKwhPorSoc)*100).toFixed(1));
                const kmKwh = b.kmRecorridos && b.totalEnergia > 0
                  ? (b.kmRecorridos / b.totalEnergia).toFixed(2)
                  : null;
                return (
                  <tr key={b.key}>
                    <td style={{color:'#555',fontSize:'12px'}}>{i+1}</td>
                    <td><div style={{color:'#4ade80',fontWeight:'500',fontSize:'12px'}}>{b.zonal}</div></td>
                    <td style={{color:'#aaa',fontSize:'12px'}}>{b.cop}</td>
                    <td style={{color:'#aaa'}}>{b.sesiones}</td>
                    <td style={{fontWeight:'600',color:cls.color}}>{b.avgKwhPorSoc}</td>
                    <td><span style={{color:diff>0?'#f87171':'#4ade80',fontSize:'12px',fontWeight:'500'}}>{diff>0?'+':''}{diffPct}%</span></td>
                    <td style={{color:'#aaa'}}>{b.avgKwProm} kW</td>
                    <td style={{color:'#aaa'}}>{b.avgEnergia} kWh</td>
                    <td style={{color:'#aaa'}}>{durStr(b.avgDuracion)}</td>
                    <td style={{color:kmKwh?'#60a5fa':'#555',fontWeight:kmKwh?'600':'400'}}>
                      {kmKwh ? `${kmKwh} km/kWh` : '—'}
                    </td>
                    <td><span style={{background:cls.bg,color:cls.color,padding:'2px 8px',borderRadius:'5px',fontSize:'11px',fontWeight:'500'}}>{cls.label}</span></td>
                  </tr>
                );
              })}
            </tbody>
          </table>
        </div>
      )}

      {/* POR RANGO SOC */}
      {tab==='rangos' && (
        <div className="card">
          <div className="card-title">Eficiencia por rango de SoC inicial — ¿cuándo carga mejor la batería?</div>
          <div style={{fontSize:'12px',color:'#555',marginBottom:'1rem'}}>
            La batería carga más eficientemente en ciertos rangos de SoC. Rangos con mayor eficiencia indican mejor estado de la batería en ese punto.
          </div>
          <ResponsiveContainer width="100%" height={250}>
            <BarChart data={porRango} barSize={60}>
              <CartesianGrid strokeDasharray="3 3" stroke="#2a2d3a" />
              <XAxis dataKey="rango" tick={{fill:'#aaa',fontSize:13}} />
              <YAxis tick={{fill:'#666',fontSize:11}} unit=" kWh/%" />
              <Tooltip content={<CustomTooltip />} />
              <ReferenceLine y={flotaKwhPorSoc} stroke="#4ade80" strokeDasharray="5 4" label={{value:`Prom: ${flotaKwhPorSoc}`,fill:'#4ade80',fontSize:11}} />
              <Bar dataKey="eficiencia" radius={[6,6,0,0]} name="kWh/% SoC"
                fill="#60a5fa"
                label={{position:'top',fill:'#aaa',fontSize:11,formatter:v=>`${v}`}} />
            </BarChart>
          </ResponsiveContainer>
          <div className="card" style={{marginTop:'1rem',margin:0}}>
            <table>
              <thead><tr><th>Rango SoC inicial</th><th>Eficiencia prom. (kWh/%)</th><th>Sesiones</th><th>Interpretación</th></tr></thead>
              <tbody>
                {porRango.map(r=>(
                  <tr key={r.rango}>
                    <td><span className="badge">{r.rango}</span></td>
                    <td style={{fontWeight:'600',color:r.eficiencia>flotaKwhPorSoc?'#f59e0b':'#4ade80'}}>{r.eficiencia}</td>
                    <td style={{color:'#aaa'}}>{r.sesiones}</td>
                    <td style={{color:'#666',fontSize:'12px'}}>
                      {r.eficiencia < 3.5 ? '✅ Muy eficiente — batería en buen estado en este rango' :
                       r.eficiencia < 4.5 ? '⚡ Normal — comportamiento esperado' :
                       '⚠ Menos eficiente — posible degradación en este rango'}
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </div>
      )}

      {/* POR MARCA */}
      {tab==='marcas' && (
        <div>
          <div className="card">
            <div className="card-title">Comparación de eficiencia por marca de bus</div>
            <ResponsiveContainer width="100%" height={220}>
              <BarChart data={porMarca} barSize={80}>
                <CartesianGrid strokeDasharray="3 3" stroke="#2a2d3a" />
                <XAxis dataKey="marca" tick={{fill:'#aaa',fontSize:13}} />
                <YAxis tick={{fill:'#666',fontSize:11}} unit=" kWh/%" />
                <Tooltip content={<CustomTooltip />} />
                <ReferenceLine y={flotaKwhPorSoc} stroke="#4ade80" strokeDasharray="5 4" />
                <Bar dataKey="eficiencia" radius={[6,6,0,0]} name="Eficiencia kWh/%"
                  fill="#a78bfa" label={{position:'top',fill:'#aaa',fontSize:12,formatter:v=>`${v}`}} />
              </BarChart>
            </ResponsiveContainer>
          </div>
          <div className="card">
            <table>
              <thead><tr><th>Marca</th><th>Sesiones</th><th>Eficiencia (kWh/%)</th><th>Potencia prom.</th><th>Energía total (kWh)</th></tr></thead>
              <tbody>
                {porMarca.map(m=>(
                  <tr key={m.marca}>
                    <td style={{fontWeight:'600',color:'#a78bfa'}}>{m.marca}</td>
                    <td style={{color:'#aaa'}}>{m.sesiones}</td>
                    <td style={{fontWeight:'600',color:m.eficiencia>flotaKwhPorSoc?'#f59e0b':'#4ade80'}}>{m.eficiencia}</td>
                    <td style={{color:'#aaa'}}>{m.potencia} kW</td>
                    <td style={{fontWeight:'500'}}>{m.energiaTotal.toLocaleString()}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </div>
      )}

      {/* DEGRADACIÓN */}
      {tab==='degradacion' && (
        <div className="card">
          <div className="card-title">Curva de degradación de batería — eficiencia promedio mensual de la flota</div>
          <div style={{fontSize:'12px',color:'#555',marginBottom:'1rem'}}>
            Si la eficiencia sube con el tiempo (más kWh por % SoC), la flota está degradando. Una línea plana o descendente indica buena salud de la batería.
          </div>
          <ResponsiveContainer width="100%" height={260}>
            <LineChart data={degradacion}>
              <CartesianGrid strokeDasharray="3 3" stroke="#2a2d3a" />
              <XAxis dataKey="mes" tick={{fill:'#aaa',fontSize:12}} />
              <YAxis tick={{fill:'#666',fontSize:11}} unit=" kWh/%" domain={['auto','auto']} />
              <Tooltip content={<CustomTooltip />} />
              <ReferenceLine y={flotaKwhPorSoc} stroke="#4ade80" strokeDasharray="5 4" label={{value:`Prom: ${flotaKwhPorSoc}`,fill:'#4ade80',fontSize:11,position:'right'}} />
              <Line type="monotone" dataKey="eficiencia" stroke="#f59e0b" strokeWidth={2.5} dot={{r:4,fill:'#f59e0b'}} name="kWh/% SoC" />
            </LineChart>
          </ResponsiveContainer>
          <div className="card" style={{marginTop:'1rem',margin:0}}>
            <table>
              <thead><tr><th>Mes</th><th>Eficiencia prom.</th><th>Sesiones</th><th>Tendencia</th></tr></thead>
              <tbody>
                {degradacion.map((d,i)=>{
                  const prev = degradacion[i-1];
                  const tendencia = prev ? (d.eficiencia > prev.eficiencia ? '↑ Degradando' : d.eficiencia < prev.eficiencia ? '↓ Mejorando' : '→ Estable') : '—';
                  const color = tendencia.includes('Degradando')?'#f87171':tendencia.includes('Mejorando')?'#4ade80':'#aaa';
                  return (
                    <tr key={d.mes}>
                      <td style={{color:'#aaa'}}>{d.mes}</td>
                      <td style={{fontWeight:'600',color:d.eficiencia>flotaKwhPorSoc?'#f59e0b':'#4ade80'}}>{d.eficiencia}</td>
                      <td style={{color:'#aaa'}}>{d.sesiones}</td>
                      <td style={{color,fontSize:'12px',fontWeight:'500'}}>{tendencia}</td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
          </div>
        </div>
      )}

      {/* DISPERSIÓN */}
      {tab==='dispersion' && (
        <div className="card">
          <div className="card-title">Potencia promedio vs Eficiencia — cada punto es un bus</div>
          <ResponsiveContainer width="100%" height={350}>
            <ScatterChart margin={{top:10,right:30,bottom:30,left:10}}>
              <CartesianGrid strokeDasharray="3 3" stroke="#2a2d3a" />
              <XAxis dataKey="x" name="kW prom." tick={{fill:'#666',fontSize:11}} label={{value:'Potencia promedio (kW)',position:'insideBottom',offset:-15,fill:'#666',fontSize:12}} />
              <YAxis dataKey="y" name="kWh/%" tick={{fill:'#666',fontSize:11}} label={{value:'kWh / % SoC',angle:-90,position:'insideLeft',fill:'#666',fontSize:12}} />
              <Tooltip content={<CustomTooltip />} />
              <ReferenceLine y={flotaKwhPorSoc} stroke="#4ade80" strokeDasharray="5 4" label={{value:`Prom: ${flotaKwhPorSoc}`,fill:'#4ade80',fontSize:11}} />
              <Scatter data={scatterData} shape={({cx,cy,payload})=>(
                <circle cx={cx} cy={cy} r={6} fill={payload.clase==='Normal'?'#4ade80':payload.clase==='Revisar'?'#f59e0b':'#f87171'} opacity={0.8} />
              )} />
            </ScatterChart>
          </ResponsiveContainer>
          <div style={{display:'flex',gap:'16px',justifyContent:'center',marginTop:'8px',fontSize:'12px'}}>
            <span><span style={{color:'#4ade80'}}>●</span> Normal (&lt;4.0)</span>
            <span><span style={{color:'#f59e0b'}}>●</span> Revisar (4-5)</span>
            <span><span style={{color:'#f87171'}}>●</span> Ineficiente (&gt;5)</span>
          </div>
        </div>
      )}

      {/* TENDENCIA */}
      {tab==='tendencia' && (
        <div className="card">
          <div className="card-title">Evolución de eficiencia por sesión</div>
          <ResponsiveContainer width="100%" height={300}>
            <LineChart data={tendencia}>
              <CartesianGrid strokeDasharray="3 3" stroke="#2a2d3a" />
              <XAxis dataKey="fecha" tick={{fill:'#666',fontSize:10}} interval={Math.floor(tendencia.length/8)} />
              <YAxis tick={{fill:'#666',fontSize:11}} unit=" kWh/%" domain={['auto','auto']} />
              <Tooltip content={<CustomTooltip />} />
              <ReferenceLine y={flotaKwhPorSoc} stroke="#4ade80" strokeDasharray="5 4" label={{value:`Prom: ${flotaKwhPorSoc}`,fill:'#4ade80',fontSize:11,position:'right'}} />
              <Line type="monotone" dataKey="kwhPorSoc" stroke="#60a5fa" strokeWidth={1.5} dot={{r:2,fill:'#60a5fa'}} name="kWh/%" />
            </LineChart>
          </ResponsiveContainer>
        </div>
      )}
    </div>
  );
}
